/**
 * The application shell.
 *
 * Locked to the viewport with no page-level scroll: the POS screen owns its own
 * scrolling regions, and a till that scrolls as a whole puts the Pay button
 * below the fold at the worst moment. That holds at every width - on a phone
 * the page still does not scroll, the region inside it does.
 *
 * Navigation has two shapes. From `lg` up the sidebar is a column beside the
 * content and the hamburger collapses it to its icons. Below `lg` the same
 * sidebar is an off-canvas drawer over the content and the hamburger opens it;
 * it is unmounted while closed rather than translated off screen, so a tab
 * press cannot land on a link nobody can see.
 *
 * The global modals live here rather than on each page because several are
 * reachable from the top bar as well as from the till - Hold and Recall are
 * opened from either.
 */

import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Sidebar from './Sidebar';
import TopBar from './TopBar';
import GlobalModals from './GlobalModals';
import PrintRoot from '../components/print/PrintRoot';
import AppFooter from '../components/ui/AppFooter';
import { selectUi, closeMobileNav } from '../store/uiSlice';
import { selectMenu } from '../store/menuSlice';
import { SpinnerBlock } from '../components/ui/Spinner';
import { useBreakpoint } from '../hooks/useMediaQuery';

const AppLayout = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { sidebarCollapsed, mobileNavOpen } = useSelector(selectUi);
    const { lookupStatus } = useSelector(selectMenu);
    const { isCompactNav } = useBreakpoint();

    // A drawer left open across a resize would sit on top of the sidebar it was
    // standing in for, so it closes the moment the layout gains room for one.
    useEffect(() => {
        if (!isCompactNav && mobileNavOpen) dispatch(closeMobileNav());
    }, [isCompactNav, mobileNavOpen, dispatch]);

    // Any navigation closes the drawer - including a redirect the user did not
    // tap, such as being bounced off a screen they cannot open.
    useEffect(() => { dispatch(closeMobileNav()); }, [location.pathname, dispatch]);

    // Nothing renders usefully before the lookups arrive - the POS grid needs
    // tax rates and payment modes to price anything at all.
    if (lookupStatus === 'loading' || lookupStatus === 'idle') {
        return (
            <div className="flex h-full items-center justify-center bg-slate-100">
                <SpinnerBlock label="Loading the till…" />
            </div>
        );
    }

    return (
        <div className="flex h-full overflow-hidden bg-slate-100">
            {/* ── Sidebar, from lg up ─────────────────────────────────── */}
            <div className="no-print hidden h-full lg:flex">
                <Sidebar collapsed={sidebarCollapsed} />
            </div>

            {/* ── The same navigation as a drawer, below lg ───────────── */}
            {isCompactNav && mobileNavOpen && (
                <div className="fixed inset-0 z-50 flex no-print lg:hidden">
                    <div
                        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px] animate-fade-in"
                        onClick={() => dispatch(closeMobileNav())}
                        aria-hidden="true"
                    />
                    {/* Capped at the viewport so the drawer never covers all of
                        it - the strip of content left showing is what tells the
                        user this is a layer they can dismiss. */}
                    <div className="relative w-[17rem] max-w-[85vw] animate-slide-in-left shadow-2xl">
                        <Sidebar collapsed={false} onNavigate={() => dispatch(closeMobileNav())} />
                    </div>
                </div>
            )}

            <div className="flex min-w-0 flex-1 flex-col">
                <TopBar />

                {/* The only scrolling region for a normal screen. The POS page
                    overrides this with its own two-pane layout. */}
                <main className="min-h-0 flex-1 overflow-hidden no-print">
                    <Outlet />
                </main>

                {/* Below lg the till pins its own cart bar to the foot of the
                    screen, right over where this would be - so the till drops
                    it there rather than give up a strip nobody can see. */}
                <AppFooter
                    className={`no-print shrink-0 border-t border-slate-200/80 bg-white px-3 pt-1.5
                                pb-[calc(0.375rem+env(safe-area-inset-bottom,0px))]
                                ${location.pathname === '/pos' ? 'hidden lg:flex' : ''}`}
                />
            </div>

            <GlobalModals />

            {/* Receipts and kitchen tickets render into here; the print
                stylesheet hides everything else. */}
            <PrintRoot />
        </div>
    );
};

export default AppLayout;
